import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, FormGroup, Table, Label, Input, Alert, Modal, ModalHeader, ModalBody, Form, ModalFooter, Badge, Progress } from 'reactstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { Field, Formik, FormikProps } from 'formik';
import toast from 'react-hot-toast';
import { useMutation } from '@apollo/client';


import { executeDeleteImage } from '../../graphql/queries/getImageUploadURL';
import useImageUpload from '../../hooks/useImageUpload';
import { useLibraryCategories } from '../../graphql/content-management/multi-library/useLibraryCategories';
import { addLibraryCategory } from '../../graphql/content-management/multi-library/addLibraryCategory';
import { updateLibraryCategory } from '../../graphql/content-management/multi-library/updateLibraryCategory';
import { removeLibraryCategory } from '../../graphql/content-management/multi-library/removeLibraryCategory';
import { useLibraryCategory } from '../../graphql/content-management/multi-library/useLibraryCategory';
import { LibraryCategory } from '../../interfaces/models/LibraryCategory';
import useVideoUpload from "../../hooks/useVideoUpload";
import { useLibraryVideos } from '../../graphql/content-management/multi-library/useLibraryVideos';
import { addLibraryVideo } from '../../graphql/content-management/multi-library/addLibraryVideo';
import { updateLibraryVideo } from '../../graphql/content-management/multi-library/updateLibraryVideo';
import { removeLibraryVideo } from '../../graphql/content-management/multi-library/removeLibraryVideo';
import { executeDeleteVideo } from "../../graphql/queries/getImageUploadURL";

import { DASHBOARD_ROOT } from '../../components/Routes/routingConstants';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import Loading from '../../components/Loading';
import { LibraryCategoryStatus, LibraryCategoryStatusOptions, LibraryVideoStatusOptions } from '../../helper/constants';
import PromptDialogs from "../../components/PromptDialog";
import TextFormInput from '../../components/Shared/Forms/TextFormInput';
import FormError from '../../components/Shared/Forms/FormError';
import ImageUploadInput from '../../components/ContentManagement/ImageUploadInput';
import ToggleableImage from '../../components/Shared/ToggleableImage';
import { LibraryVideo } from '../../interfaces/models/LibraryVideo';
import VideoUploadInput from '../../components/Shared/Forms/VideoUploadInput';
import { getVideoUrlFromCloudflareId } from '../../helper/getVideoUrlFromCloudflareId';
import AddLibraryVideoValidator from '../../validators/AddLibraryVideoValidator';
import AddLibraryCategoryValidator from '../../validators/AddLibraryCategoryValidator';
import LibraryParentCategorySelector from '../../components/MultiLibrary/LibraryParentCategorySelector';
import OrganisationSelectorModal from '../../components/ContentManagement/OrganisationSelectorModal';
import { Organisation } from '../../interfaces/models/Organisation';
import { useOrganisation } from '../../hooks/useOrganisation';

interface ModalLabels { 
  name: string;
  buttonName: string;
  action: string;
}

interface FormDataCategory {
  name: string;
  description: string;
  status: number;
  image_id: string;
  image_name: string;
  parent_id?: string;
}

interface FormDataVideo {
  name: string;
  description: string;
  status: number;
  video_name: string;
  video_id: string;
  library_category_id: string;
}


export const MultiLibraryCategoryEditor = () => {
  const breadcrumbsData = [
    { name: 'Content List', path: `${DASHBOARD_ROOT}/content-management/` },
    { name: 'Multi Library', path: `${DASHBOARD_ROOT}/content-management/multi-library` },
  ];
  
  const history = useHistory();
  
  const query = new URLSearchParams(useLocation().search);
  const id = query.get("id");
  const { data: categoryData,refetch: refetchCategory } = useLibraryCategory(id);
  const { data, loading, error, refetch } = useLibraryCategories(id);
  const { data: videoData, loading: videoLoading, refetch: refetchVideo } = useLibraryVideos(id);
  
  const { data: organisationsData, loading: organisationsLoading } = useOrganisation();

  const [isOnProcess, setIsOnProcess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isParentSelectorOpen, setIsParentSelectorOpen] = useState(false);
  const [selectedNewParent, setSelectedNewParent] = useState<LibraryCategory>(null);

  const [categoryBreadcrumbsData, setCategoryBreadcrumbsData] = useState<Array<{ name: string, path: string }>>([{ name: "Library", path: `${DASHBOARD_ROOT}/content-management/multi-library` }]);
  const [currentCategory, setCurrentCategory] = useState<LibraryCategory>(null);
  const [categories, setCategories] = useState<LibraryCategory[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalLabels, setModalLabels] = useState<ModalLabels>({
    name: '',
    buttonName: '',
    action: '',
  });
  const formikRef = useRef<FormikProps<FormDataCategory>>(null);
  const [initialValue, setInitialValue] = useState<FormDataCategory>({
    name: '',
    description: '',
    status: LibraryCategoryStatus.ACTIVE,
    image_id: '',
    image_name: '',
  });
  const [selectedCategory, setSelectedCategory] = useState<LibraryCategory>(null);
  const [isDeleteCategoryModalOpen, setIsDeleteCategoryModalOpen] = useState<boolean>(false);

  const [blobFile, setBlobFile] = useState<File>(null);
  const [url, setUrl] = useState<string>("");
  const [fileId, setFileId] = useState<string>("");
  const imageInputRef = useRef<{ clearImageField: () => void, refetchUrl: () => void }>(null);

  const [selectedVideo, setSelectedVideo] = useState<LibraryVideo>(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);
  const [isDeleteVideoModalOpen, setIsDeleteVideoModalOpen] = useState<boolean>(false);
  const [videos, setVideos] = useState<LibraryVideo[]>([]);

  const [isSetupModalOpen, setIsSetupModalOpen] = useState<boolean>(false);
  const [isForCategory, setIsForCategory] = useState<boolean>(true);
  const [selectedOrganisations, setSelectedOrganisations] = useState<string[]>([]);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);

  const videoFormikRef = useRef<FormikProps<FormDataVideo>>(null);
  const [initialVideoValue, setInitialVideoValue] = useState<FormDataVideo>({
    name: '',
    description: '',
    status: LibraryCategoryStatus.ACTIVE,
    video_name: '',
    video_id: '',
    library_category_id: ''
  });
  const [modalVideoLabels, setModalVideoLabels] = useState<ModalLabels>({
    name: 'Add Video',
    buttonName: 'Add',
    action: 'add',
  });
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [videoFileId, setVideoFileId] = useState<string>("");
  const [videoBlob, setVideoBlob] = useState<File>(null);
  const videoInputRef = useRef<{ 
    clearVideoField: () => void, 
    refetchUrl : () => Promise<string>
  }>(null);

  const { uploadImage } = useImageUpload();
  const [deleteFile] = useMutation(executeDeleteImage);
  const [deleteVideo] = useMutation(executeDeleteVideo);
  const { uploadVideo, uploadingVideoProgress, uploadingVideo } = useVideoUpload();

  const [addLibraryCategoryHook] = useMutation(addLibraryCategory,{
    onCompleted: () =>{
      toast.success("Category was successfully added");
    },
    onError: (err) =>{
      toast.error(err.graphQLErrors[0].message);
    }
  });

  const [updateLibraryCategoryHook] = useMutation(updateLibraryCategory,{
    onCompleted: () =>{
      toast.success("Category was successfully updated");
    },
    onError: (err) =>{
      toast.error(err.graphQLErrors[0].message);
    }
  });

  const [removeLibraryCategoryHook] = useMutation(removeLibraryCategory,{
    onCompleted: () =>{
      toast.success("Category was successfully deleted");
    },
    onError: () =>{
      toast.error("Something went wrong whilst deleting category. Please try again or if issue persists, please contact us");
    }
  });

  const [addLibraryVideoHook] = useMutation(addLibraryVideo,{
    onCompleted: () =>{
      toast.success("Video was successfully added");
    },
    onError: (err) =>{
      toast.error(err.graphQLErrors[0].message);
    }
  });

  const [updateLibraryVideoHook] = useMutation(updateLibraryVideo,{
    onCompleted: () =>{
      toast.success("Video was successfully updated");
    },
    onError: (err) =>{
      toast.error(err.graphQLErrors[0].message);
    }
  });

  const [removeLibraryVideoHook] = useMutation(removeLibraryVideo,{
    onCompleted: () =>{
      toast.success("Video was successfully deleted");
    },
    onError: (err) =>{
      toast.error(err.graphQLErrors[0].message);
    }
  });

  const ToggleParentSelector = () => {
    setIsParentSelectorOpen((prev) => !prev);
  }

  const OnSelectParentSelector = (category: LibraryCategory) => {
    console.log(category);
    setSelectedNewParent(category);
    formikRef.current?.setFieldValue("parent_id", category._id);
    videoFormikRef.current?.setFieldValue("library_category_id", category._id);
  }

  const ReloaderCategory = useCallback(async () => {
    setIsLoading(true);
    await refetch({
      filter: {
        parent_id: id,
      },
    })
    setIsLoading(false);
  }, [id, refetch]);

  const ReloaderVideo = useCallback(async () => {
    setIsLoading(true);
    await refetchVideo({
      filter: {
        library_category_id: id,
      },
    })
    setIsLoading(false);
  }, [id, refetchVideo]);
  
  const DeleteFileExecute = async (fileIdCloud: string) => {
    if(!fileIdCloud){
      return; 
    }
    try{
      await deleteFile({
        variables: {
          fileId: fileIdCloud
        },
      })
    }
    catch(err){
      console.error(err);
    }
  }

  const DeleteVideoExecute = async (videoIdCloud: string) => {
    if(!videoIdCloud){
      return; 
    }
    try{
      await deleteVideo({
        variables: {
          fileId: videoIdCloud
        },
      })
    }
    catch(err){
      console.error(err);
    }
  }

  const HandleImageChange = (url: string, fileId: string, blob: File) => {
    setUrl(url);
    setFileId(fileId);
    setBlobFile(blob);
  }

  const IsFetchingToggle = (isGetting: boolean) =>{
    setIsOnProcess(isGetting);
  }

  useEffect(() => {
    if (categoryData) {
      setCurrentCategory(categoryData.library_category);
    } 
  }, [categoryData]);

  useEffect(() => {
    if (videoData) {
      setVideos(videoData.library_videos);
    }
  }, [videoData]);

  useEffect(() => {
    if (data) {
      setCategories(data.library_categories);
    }
  }, [data]);

  useEffect(() => {
    const refetchData = async () => {
      await ReloaderCategory();
      const categoryData = await refetchCategory({
        _id: id,
      });
      if(categoryData?.data) {
        setCurrentCategory(categoryData.data?.library_category);
      }
    }
    if (id) {
      refetchData();
    }
    else {
      setCurrentCategory(null);
    }
  }, [id, ReloaderCategory, refetchCategory]);

  useEffect(() => {
    if (currentCategory) {
      const categoryBreadcrumbsData: Array<{ name: string, path: string }> = [];
      categoryBreadcrumbsData.push({ name: "Library", path: `${DASHBOARD_ROOT}/content-management/multi-library` });
      for(let i = 0; i < currentCategory.parents.length; i++) {
        const parent = currentCategory.parents[i];
        categoryBreadcrumbsData.push({ name: parent.name, path: `${DASHBOARD_ROOT}/content-management/multi-library?id=${parent._id}` });
      }
      categoryBreadcrumbsData.push({ name: currentCategory.name, path: `${DASHBOARD_ROOT}/content-management/multi-library?id=${currentCategory._id}` });
      setCategoryBreadcrumbsData(categoryBreadcrumbsData);
    }
    else {
      setCategoryBreadcrumbsData([{ name: "Library", path: `${DASHBOARD_ROOT}/content-management/multi-library` }]);

    }
  }, [currentCategory]);

  useEffect(() => {
    if (organisationsData) {
      setOrganisations(organisationsData.organisations);
    }
  }, [organisationsData]);

//#region CRUD Category Functions
  const HandleView = (category: LibraryCategory) => {
    history.push(`${DASHBOARD_ROOT}/content-management/multi-library?id=${category._id}`);
  }

  const HandleOpenAdd = () => {
    setModalLabels({
      name: 'Add Category',
      buttonName: 'Add',
      action: 'add',
    });
    setInitialValue({
      name: '',
      description: '',
      status: LibraryCategoryStatus.ACTIVE,
      image_id: '',
      image_name: '',
      parent_id: id,
    });
    setSelectedNewParent(null);
    setIsModalOpen(true);
  }

  const HandleOpenEdit = (category: LibraryCategory) => {
    setModalLabels({
      name: 'Edit Category',
      buttonName: 'Edit',
      action: 'edit',
    });
    setInitialValue({
      name: category.name,
      description: category.description,
      status: category.status,
      image_id: category.image_id,
      image_name: category.image_name,
      parent_id: id,
    });
    setSelectedNewParent(null);
    setSelectedCategory(category);
    setIsModalOpen(true);
  }

  const handleToggleModal = () => {
    if(isModalOpen){
      imageInputRef.current?.clearImageField();
    }
    setIsModalOpen(prev => !prev);
  }

  const HandleSubmit = async (formData: FormDataCategory) => {
    try{
      if(isOnProcess) return;
      setIsOnProcess(true);
      if(blobFile) {
        const isUploaded = await uploadImage(url,blobFile);
        if(!isUploaded){
          toast.error("Something went wrong whilst uploading your image. Please try again or if issue persists, please contact us");
          setIsOnProcess(false);
          return;
        }
      }

      let result = false;

      if(modalLabels.action === "add") {
        result = await addLibraryCategoryHook({
          variables: {
            record: {
              ...formData,
              image_id: fileId,
              status: Number(formData.status),
              parent_id: id || undefined,
              parent_ids: currentCategory ? [...currentCategory.parent_ids, id] : undefined,
            }
          }
        }).then((res) => res.errors ? false: true).catch(()=>false);
      }
      else if(modalLabels.action === "edit") {
        result = await updateLibraryCategoryHook({
          variables: {
            _id: selectedCategory._id,
            record: {
              ...formData,
              image_id: fileId || selectedCategory.image_id,
              status: Number(formData.status),
            }
          }
        }).then((res) => res.errors ? false: true).catch(()=>false);

        if(result) {
          if(fileId && selectedCategory.image_id) {
            await DeleteFileExecute(selectedCategory.image_id);
          }
        }
      }

      if(!result) {
        await DeleteFileExecute(fileId);
        imageInputRef.current.refetchUrl();
        setIsOnProcess(false);
        return;
      }

      await ReloaderCategory();
      imageInputRef.current.clearImageField();
      setIsOnProcess(false);
      setIsModalOpen(false);
    }
    catch(err){
      console.error(err);
      setIsOnProcess(false);
    }
  }

  const HandleOpenDelete = (category: LibraryCategory) => {
    setSelectedCategory(category);
    setIsDeleteCategoryModalOpen(true);
  }

  const HandleCategoryDelete = async () => {
    try{
      if(isOnProcess) return;
      setIsOnProcess(true);
      const result = await removeLibraryCategoryHook({
        variables: {
          _id: selectedCategory._id
        }
      }).then((res) => res.errors ? false: true).catch(()=>false);

      if(!result) {
        setIsOnProcess(false);
        return;
      }
      await ReloaderCategory();
      setIsOnProcess(false);
      setIsDeleteCategoryModalOpen(false);
    }
    catch(err){
      console.error(err);
    }
  }
//#endregion CRUD Functions
//#region CRUD Video Functions
  const HandleOpenAddVideo = () => {
    setModalVideoLabels({
      name: 'Add Video',
      buttonName: 'Add',
      action: 'add',
    });
    setInitialVideoValue({
      name: '',
      description: '',
      status: LibraryCategoryStatus.ACTIVE,
      video_name: '',
      video_id: '',
      library_category_id: id,
    });
    
    setSelectedNewParent(null);
    setIsVideoModalOpen(true);
  }

  const HandleToggleVideoModal = () => {
    if(isVideoModalOpen){
      videoInputRef.current?.clearVideoField();
    }
    setIsVideoModalOpen(prev => !prev);
  }

  const handleOpenEditVideo = (video: LibraryVideo) => {
    setModalVideoLabels({
      name: 'Edit Video',
      buttonName: 'Edit',
      action: 'edit',
    });
    setInitialVideoValue({
      name: video.name,
      description: video.description,
      status: video.status,
      video_name: video.video_name,
      video_id: getVideoUrlFromCloudflareId(video.video_id),
      library_category_id: id,
    });
    
    setSelectedNewParent(null);
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
  }

  const handleOpenDeleteVideo = (video: LibraryVideo) => {
    setSelectedVideo(video);
    setIsDeleteVideoModalOpen(true);
  }

  const HandleUploadFile : () => Promise<string> = async () => {
    return new Promise(async (resolve, reject) => {
        if(!videoBlob) { return resolve(null); }
        return await uploadVideo(videoUrl, videoBlob, {
          onError: () => {
            toast.error("Something went wrong whilst uploading your Video. Please try again or if issue persists, please contact us");
            reject(null);
          },
          onSuccess: () => {
            resolve(videoFileId);
          },
        });
    })
  }

  const getLastPart = (url : string) => {
    if(!url.includes('https://') && !url.includes('http://')) return url;
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

  const HandleDeleteVideo = async () => {
    try{
      if(isOnProcess) return;
      setIsOnProcess(true);
      const result = await removeLibraryVideoHook({
        variables: {
          _id: selectedVideo._id
        }
      }).then((res) => res.errors ? false: true).catch(()=>false);
      if(!result) {
        setIsOnProcess(false);
        return;
      }
      await ReloaderVideo();
      setIsOnProcess(false);
      setIsDeleteVideoModalOpen(false);
    }
    catch(err){
      console.error(err);
    }
  }

  const HandleSubmitVideo = async (formData: FormDataVideo) => {
    try{
      if(isOnProcess) return;
      setIsOnProcess(true);
      if(videoBlob) {
        const isUploaded = await HandleUploadFile();
        if(!isUploaded){
          toast.error("Something went wrong whilst uploading your video. Please try again or if issue persists, please contact us");
          setIsOnProcess(false);
          return;
        }
      }

      let result = false;

      if(modalVideoLabels.action === "add") {
        result = await addLibraryVideoHook({
          variables: {
            record: {
              ...formData,
              status: Number(formData.status),
              library_category_id: id,
              from_reference: videoBlob ? false : true,
              video_id:  videoFileId || getLastPart(formData.video_id),
            }
          }
        }).then((res) => res.errors ? false: true).catch(()=>false);
      }
      else if(modalVideoLabels.action === "edit") {

        const fromReference = videoBlob ? false : true;
        const getVideoId = videoFileId || getLastPart(formData.video_id);
        result = await updateLibraryVideoHook({
          variables: {
            _id: selectedVideo._id,
            record: {
              ...formData,
              status: Number(formData.status),
              from_reference: fromReference,
              video_id: getVideoId,
            }
          }
        }).then((res) => res.errors ? false: true).catch(()=>false);

        if(result) {
          if(getVideoId !== selectedVideo.video_id && selectedVideo.from_reference === false) {
            await DeleteVideoExecute(selectedVideo.video_id);
          }
        }

      }

      if(!result) {
        await DeleteVideoExecute(videoFileId);
        videoInputRef.current.refetchUrl();
        setIsOnProcess(false);
        return;
      }

      await ReloaderVideo();
      videoInputRef.current.clearVideoField();
      setIsOnProcess(false);
      setIsVideoModalOpen(false);
    }
    catch(err){
      console.error(err);
      setIsOnProcess(false);
    }
  }
//#endregion CRUD Video Functions


//#region Setup Organisation for Category and Video

const OpenModalSetupCategory = (category: LibraryCategory) => {
  setIsSetupModalOpen(true);
  setIsForCategory(true);
  setSelectedOrganisations(category.using_organisation_ids || []);
  setSelectedCategory(category);
}

const OpenModalSetupVideo = (video: LibraryVideo) => {
  setIsSetupModalOpen(true);
  setIsForCategory(false);
  setSelectedOrganisations(video.using_organisation_ids || []);
  setSelectedVideo(video);
}

const HandleSaveSelectedOrganisation = async (selected: string[]) => {
  setIsOnProcess(true);

  let result = false;
  if(isForCategory) {
    result = await updateLibraryCategoryHook({
      variables: {
        _id: selectedCategory._id,
        record: {
          using_organisation_ids: selected,
        },
      },
    }).then((res) => res.errors ? false: true).catch(()=>false);
  }
  else {
    result = await updateLibraryVideoHook({
      variables: {
        _id: selectedVideo._id,
        record: {
          using_organisation_ids: selected,
        },
      },
    }).then((res) => res.errors ? false: true).catch(()=>false);
  }

  if(!result) {
    setIsOnProcess(false);
    return;
  }

  if(isForCategory) {
    await ReloaderCategory();
  }
  else {
    await ReloaderVideo();
  }
  setIsOnProcess(false);
  setIsSetupModalOpen(false);
}
//#endregion Setup Organisation for Category and Video

  return (
  <>
    <Breadcrumbs
      crumbs={breadcrumbsData}
    />

    {
      error && <Alert color="warning" >Sorry! Error on getting categories</Alert>
    }
    <div className="row mb-3">
      <div className="col-md-8  mt-1">
        <FormGroup className="d-flex align-items-center">
          <Label className="mr-2">Search: </Label>
          <Input type="text" placeholder="Search by name" />
        </FormGroup>
      </div>
      <div className="col-md-4  mt-1">
        {
          id ?
          <div className="d-flex">
            {
              videos.length === 0 &&
              <Button color="primary" disabled={videoLoading || loading} className="w-100 ml-2" onClick={HandleOpenAdd}>Add Sub Category</Button>
            }
            {
              categories.length === 0 &&
              <Button color="primary" disabled={videoLoading || loading} className="w-100" onClick={HandleOpenAddVideo}>Add Video</Button>
            }
          </div>
          :
          <div className="d-flex justify-content-end">
            <Button color="primary" disabled={videoLoading || loading} className="w-50" onClick={HandleOpenAdd}>Add Category</Button>
          </div>
        }
      </div>
    </div>

    
    <Breadcrumbs
      className="breadcrumb-bg-transparent-no-padding"
      crumbs={categoryBreadcrumbsData}
    />

    {
      videos.length > 0 ?
      <Table bordered className="bg-white">
        <thead>
          <tr>
            <th>Video</th>
            <th>Name</th>
            <th>Description</th>
            <th>Action</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {
            loading || isLoading ? (
              <tr>
                <td colSpan={5} className="text-center">
                  <Loading />
                </td>
              </tr>
            )
            : 
            videos.map((video) => (
              <tr key={video._id}>
                <td className="text-center">
                  <ToggleableImage image={getVideoUrlFromCloudflareId(video.video_id)} fromVideo={true} />
                </td>
                <td>{video.name}</td>
                <td>{video.description}</td>
                <td>
                  <Badge color={LibraryVideoStatusOptions[video.status - 1].color}>{LibraryVideoStatusOptions[video.status - 1].name}</Badge>
                </td>
                <td>
                  <Button color="primary" size="sm" onClick={() => handleOpenEditVideo(video)}>Edit</Button>
                  {
                    currentCategory && currentCategory.using_organisation_ids.length  === 0 &&
                    <Button color="primary" size="sm" disabled={organisationsLoading} onClick={()=>{ OpenModalSetupVideo(video) }} > Organisations </Button>
                  }
                  <Button color="danger" className="btn btn-danger" size="sm" onClick={() => handleOpenDeleteVideo(video)}>Delete</Button>
                </td>
              </tr>
            ))
          }
        </tbody>
      </Table>
      :
      <Table bordered responsive className="bg-white">
        <thead>
          <tr>
            <th>Image</th>
            <th>Name</th>
            <th>Description</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {
            loading || isLoading ? (
              <tr>
                <td colSpan={5} className="text-center">
                  <Loading />
                </td>
              </tr>
            ): 
            categories.length === 0 ? 
            (
              <tr>
                <td colSpan={5} className="text-center">
                  No categories found
                </td>
              </tr>
            ) 
          : 
            (
              categories.map((category) => (
                <tr key={category._id}>
                  <td className="text-center">
                    {category.image_id && <ToggleableImage image={category.image_id} />}
                  </td>
                  <td>{category.name}</td>
                  <td>{category.description}</td>
                  <td>
                    <Badge color={LibraryCategoryStatusOptions[category.status - 1].color}>{LibraryCategoryStatusOptions[category.status - 1].name}</Badge>
                  </td>
                  <td>
                    <Button color="primary" size="sm" onClick={() => HandleView(category)}>Open</Button>
                    <Button color="primary" size="sm" onClick={() => HandleOpenEdit(category)}>Edit</Button>
                    {
                      ((currentCategory && currentCategory.using_organisation_ids.length === 0) || !currentCategory) &&
                      <Button color="primary" disabled={organisationsLoading} size="sm" onClick={()=>{ OpenModalSetupCategory(category) }} > Organisations </Button>
                    }
                    <Button color="danger" className="btn btn-danger" size="sm" onClick={() => HandleOpenDelete(category)}>Delete</Button>  
                  </td>
                </tr>
              ))
            )
          }
        </tbody>
      </Table>
    }

    

    <Modal
      id="multi-library-category-edit-modal"
      backdrop={true}
      isOpen={isModalOpen}
    >
      <ModalHeader toggle={handleToggleModal} className="pb-0">
          <p className="text-lg">{modalLabels.name}</p>
      </ModalHeader>
      <ModalBody>
        <Formik
          innerRef={formikRef}
          initialValues={initialValue}
          validationSchema={AddLibraryCategoryValidator}
          onSubmit={async (value: FormDataCategory) => {
            await HandleSubmit(value);
          }}
        >
          {({errors, touched}) => (
            <Form>
              <fieldset disabled={isOnProcess}>
                <TextFormInput
                  name="name"
                  placeholder="Enter Name"
                  label="Name"
                  required={true}
                />
                <div className="form-group mb-0">
                  <label>Description *</label>
                  <Field
                      className="form-control"
                      name="description"
                      as="textarea"
                      rows={3}
                      placeholder="Description of the category"
                  />
                  <FormError>{touched.description && (errors.description)}</FormError>
                </div>
                <FormGroup className={"mb-0" + (modalLabels.action === "add" ? " d-none": "")}>
                  <Label className="mr-2">Status * </Label>
                  <Field 
                    className="form-control" 
                    as="select" 
                    name="status"
                  >
                    {
                      LibraryCategoryStatusOptions.map((status) => (
                        <option key={status.value} value={status.value}>{status.name}</option>
                      ))
                    }
                  </Field>
                  <FormError>{touched.status && (errors.status)}</FormError>
                </FormGroup>
                <ImageUploadInput
                  ref={imageInputRef}
                  formikRef={formikRef}
                  transferFunction={HandleImageChange}
                  isGettingUrl={IsFetchingToggle}
                  imageName="image_name"
                  imageIdName="image_id"
                  extensionAllowed="image/jpeg,image/jpg,image/png"
                />
                {
                  modalLabels.action === "edit"  &&
                  <FormGroup className="mb-0">
                    <Label className="mr-2">Parent Category </Label>
                    <Input type="text" disabled={true} value={selectedNewParent?.name || currentCategory?.name || "No Parent"} />
                    <span className="text-primary" role="button" onClick={ToggleParentSelector}>Select Parent Category</span>
                  </FormGroup>
                }


              </fieldset>
            </Form>
          )}
        </Formik>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" disabled={isOnProcess} onClick={handleToggleModal}>Close</Button>
        <Button color="primary" disabled={isOnProcess} onClick={() => { formikRef?.current.submitForm() }}>{modalLabels.buttonName}</Button>
      </ModalFooter>
    </Modal>

    <Modal
      id="multi-library-category-video-edit-modal"
      backdrop={true}
      isOpen={isVideoModalOpen}
    >
      <ModalHeader toggle={HandleToggleVideoModal} className="pb-0">
          <p className="text-lg">{modalVideoLabels.name}</p>
      </ModalHeader>
      <ModalBody>
        <Formik
          innerRef={videoFormikRef}
          initialValues={initialVideoValue}
          validationSchema={AddLibraryVideoValidator}
          onSubmit={async (value: FormDataVideo) => {
            await HandleSubmitVideo(value);
          }}
        >
          {({errors, touched}) => (

            <Form>
              <fieldset disabled={isOnProcess}>
                <TextFormInput
                  name="name"
                  placeholder="Enter Name"
                  label="Name"
                  required={true}
                />
                <FormGroup className="mb-0">
                  <Label className="mr-2">Description * </Label>
                  <Field className="form-control" as="textarea" name="description" rows={3} placeholder="Description of the video" />
                  <FormError>{touched.description && (errors.description)}</FormError>
                </FormGroup>
                <FormGroup className="mb-0">
                  <Label className="mr-2">Status * </Label>
                  <Field className="form-control" as="select" name="status">
                    {
                      LibraryCategoryStatusOptions.map((status) => (
                        <option key={status.value} value={status.value}>{status.name}</option>
                      ))
                    }
                  </Field>
                </FormGroup>
                <VideoUploadInput
                  ref={videoInputRef}
                  formikRef={videoFormikRef}
                  transferFunction={(url, cloudflareId, blob) => {
                    setVideoUrl(url);
                    setVideoFileId(cloudflareId);
                    setVideoBlob(blob);
                  }}
                  isGettingUrl={setIsOnProcess}
                  videoName="video_name"
                  videoUrlName="video_id"
                  formGroupClassName="mt-3 mb-3"
                  required={false}
                  label="Video"
                  maxMb={7_168}
                />
                {uploadingVideo && (
                  <>
                    <Progress value={uploadingVideoProgress} />
                    <p>Uploading video ({uploadingVideoProgress}%)...</p>
                  </>
                )}
                {
                  modalVideoLabels.action === "edit"  &&
                  <FormGroup className="mb-0">
                    <Label className="mr-2">Parent Category * </Label>
                    <Input type="text" disabled={true} value={selectedNewParent?.name || currentCategory?.name || "No Parent"} />
                    <FormError>{errors.library_category_id}</FormError>
                    <span className="text-primary" role="button" onClick={ToggleParentSelector}>Select Parent Category</span>
                    
                  </FormGroup>
                }
              </fieldset>

            </Form>
          ) 
        }
        </Formik>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" disabled={isOnProcess} onClick={HandleToggleVideoModal}>Close</Button>
        <Button color="primary" disabled={isOnProcess} onClick={() => { videoFormikRef?.current.submitForm() }}>{modalVideoLabels.buttonName}</Button>
      </ModalFooter>
    </Modal>

    <PromptDialogs
      isOpen={isDeleteCategoryModalOpen}
      onCancel={() => { setIsDeleteCategoryModalOpen(false); }}
      onConfirm={HandleCategoryDelete}
      title="Delete Category"
      promptMessage="Are you sure you want to delete this Category?"
      confirmBtnTxt="Delete"
      isOnProcess={isOnProcess}
    />

    <PromptDialogs
      isOpen={isDeleteVideoModalOpen}
      onCancel={() => { setIsDeleteVideoModalOpen(false); }}
      onConfirm={HandleDeleteVideo}
      title="Delete Video"
      promptMessage="Are you sure you want to delete this Video?"
      confirmBtnTxt="Delete"
      isOnProcess={isOnProcess}
    />

    <OrganisationSelectorModal
      toggleModal={() => setIsSetupModalOpen((prev) => !prev)}
      modalOpen={isSetupModalOpen}
      handleSave={HandleSaveSelectedOrganisation}
      organisations={organisations}
      isButtonDisabled={isOnProcess}
      selectedOrgs={selectedOrganisations}
    />

    {
      isParentSelectorOpen &&
      <LibraryParentCategorySelector
        onSelect={OnSelectParentSelector}
        toggleModal={ToggleParentSelector}
        modalOpen={isParentSelectorOpen}
      /> 
    }
  </>
  )

}
;